/**
 * The Data Room vocabulary: the project's versioned Markdown documents.
 *
 * Pure and total, like `tasks.ts`, and built on the same two disciplines:
 * the role question is answered by the capability matrix and nowhere else,
 * and every vocabulary is closed — a category that is not in the list below
 * is a compile error in TypeScript and a foreign-key violation in Postgres,
 * never a silently accepted string.
 *
 * What the Data Room deliberately is not:
 *
 *   * **Not a filesystem.** Categories are one flat closed set. There is no
 *     nesting, no folder tree, no move operation — six categories cover a
 *     three-person team's canonical documents, and a hierarchy would grow
 *     faster than the content it organises.
 *   * **Not Git.** A document is a head row plus an append-only run of
 *     revisions. There are no branches, no merges, and restoring an old
 *     revision *copies it forward* as a new revision rather than rewriting
 *     which version is current — pivots stay traceable in one linear history.
 *   * **Not a knowledge base.** No comments, no mentions, no embeddings, no
 *     search infrastructure. Finding a document is reading a short list.
 */
import { humanRoleMay } from './human-authorization.js';
// --------------------------------------------------------------------------- //
// Closed vocabularies — these mirror the reference tables in migration 0012
// --------------------------------------------------------------------------- //
/** The Data Room categories, in shelf order. */
export const DATA_ROOM_CATEGORIES = [
    'PRODUCT',
    'DIRECTION',
    'DESIGN',
    'TECHNOLOGY',
    'TASK_OUTPUTS',
    'FUTURE',
];
export function isDataRoomCategory(value) {
    return (typeof value === 'string' &&
        DATA_ROOM_CATEGORIES.includes(value));
}
/**
 * The export folder for each category.
 *
 * Fixed English constants rather than localised labels, because the ZIP a
 * download produces is a portable artifact whose paths must be stable across
 * whoever pressed the button. They are also the only path segments in an
 * export that are not a slug, and every one of them is a safe literal.
 */
export const DATA_ROOM_EXPORT_FOLDERS = {
    PRODUCT: 'Product',
    DIRECTION: 'Direction',
    DESIGN: 'Design',
    TECHNOLOGY: 'Technology',
    TASK_OUTPUTS: 'Task Outputs',
    FUTURE: 'Future',
};
// --------------------------------------------------------------------------- //
// Bounds — these mirror the CHECK constraints in migration 0012
// --------------------------------------------------------------------------- //
/** Same bound as a task title, for the same reason: it is a list label. */
export const DATA_ROOM_MAX_TITLE_LENGTH = 200;
/**
 * Three times a task description. A PRD is longer than a task, but a
 * document that outgrows this bound has outgrown one document — split it.
 * The transport can carry it: sixty thousand characters of Arabic is about
 * 120 KiB of UTF-8, inside the API's 256 KiB body posture.
 */
export const DATA_ROOM_MAX_CONTENT_LENGTH = 60_000;
/** A change note is a commit message, not a second document. */
export const DATA_ROOM_MAX_CHANGE_NOTE_LENGTH = 500;
/** Slugs are bounded so export paths and URLs stay readable. */
export const DATA_ROOM_MAX_SLUG_LENGTH = 80;
/** A folder name is a navigation label, not a document title. */
export const DATA_ROOM_MAX_FOLDER_NAME_LENGTH = 100;
/**
 * The deepest level a folder may occupy beneath the Data Room root.
 *
 * The category root is level 1, so user folders occupy levels 2–4 — three
 * nested levels. A three-person team's documents that need more hierarchy
 * than that need fewer folders, not a deeper tree.
 */
export const DATA_ROOM_MAX_FOLDER_DEPTH = 4;
// --------------------------------------------------------------------------- //
// Authority
// --------------------------------------------------------------------------- //
/**
 * May this role enter the Data Room at all?
 *
 * One question covering every surface — listing, reading, writing, history,
 * restore, task linking, and export. Callers map a refusal to the same
 * response a missing page produces.
 */
export function mayUseDataRoom(role) {
    return humanRoleMay(role, 'data_room');
}
/**
 * May this role move a *folder*?
 *
 * The Owner alone, and for a reason that is about documents rather than
 * folders. Moving across categories re-shelves the whole subtree, documents
 * included, so it belongs to the one role that can see everything it touches.
 *
 * Creating and renaming a folder are *not* covered: neither touches a
 * document, so both stay open to everyone the Data Room admits.
 */
export function mayRestructureDataRoom(role) {
    return humanRoleMay(role, 'administer_contributors');
}
/**
 * May this role recursively delete a folder tree?
 *
 * Owner and Developer may. Documents are soft-deleted rather than destroyed,
 * while arbitrary moves remain a separate Owner-only structural authority.
 */
export function mayDeleteDataRoomFolder(role) {
    return role === 'OWNER' || role === 'DEVELOPER';
}
// --------------------------------------------------------------------------- //
// Slugs
// --------------------------------------------------------------------------- //
/**
 * The stable identifier a document's title suggests.
 *
 * Lowercase ASCII letters, digits and single hyphens — nothing else survives,
 * which is what makes a slug safe to place in a URL and in an export path
 * without a sanitiser at the point of use. A title with no usable characters
 * (an all-Arabic title, for instance) falls back to `document`; uniqueness is
 * the caller's job, because only the store knows what exists.
 */
export function slugForDocumentTitle(title) {
    const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, DATA_ROOM_MAX_SLUG_LENGTH)
        .replace(/-+$/, '');
    return slug.length > 0 ? slug : 'document';
}
//# sourceMappingURL=data-room.js.map